import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  ArrowLeft,
  Clock,
  ExternalLink,
  FileText,
  Info,
  MessageSquare,
  Star,
  User,
} from "lucide-react";
import { useScheduleChangeLogQuery } from "../api/queries";

const sessionTypeLabels: Record<string, string> = {
  class_session: "ຄລາດສ໌",
  private_lesson_booking: "ວິຊາສ່ວນຕົວ",
  room_booking: "ຈອງຫ້ອງ",
};

const changeTypeLabels: Record<string, string> = {
  time: "ປັບເວລາ",
  room: "ປ່ຽນຫ້ອງ",
  instructor: "ປ່ຽນຄູຜູ້ສອນ",
  capacity: "ປ່ຽນຄວາມຈຸ",
  other: "ອື່ນໆ",
};

function formatLogId(id: string): string {
  const short = id.replace(/-/g, "").slice(-8).toUpperCase();
  return `#SCH-${short}`;
}

export function ScheduleChangeLogDetailPage() {
  const search = useSearch({ from: "/app/classes/schedule-logs/$id" });
  const nav = useNavigate({ from: "/app/classes/schedule-logs/$id" });
  const { id } = useParams({ from: "/app/classes/schedule-logs/$id" });

  const { data, ...result } = useScheduleChangeLogQuery(id ?? "");
  const item = data;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ບັນທຶກການປ່ຽນແປງຕາຕະລາງ", to: "/app/classes/schedule-logs" },
            { label: "ລາຍລະອຽດ" },
          ]}
        />
      </Header>

      <Main>
        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດບັນທຶກ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!item ? null : (
            <>
              {/* Page title + actions */}
              <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-bold text-2xl tracking-tight">
                    ລາຍລະອຽດບັນທຶກ: {formatLogId(item.id)}
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    ບັນທຶກການປ່ຽນແປງຕາຕະລາງດ້ວຍມື
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      nav({
                        to: "/app/classes/schedule-logs",
                        search: { ...search },
                      })
                    }
                  >
                    <ArrowLeft className="mr-1 size-4" />
                    ກັບຄືນ
                  </Button>
                  {item.entityLink ? (
                    <a href={item.entityLink}>
                      <Button size="sm" type="button">
                        <ExternalLink className="mr-1 size-4" />
                        ເບິ່ງຕາຕະລາງ
                      </Button>
                    </a>
                  ) : null}
                </div>
              </div>

              {/* Two columns */}
              <div className="grid gap-6 md:grid-cols-3">
                {/* Left column */}
                <div className="space-y-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Info className="size-4 text-muted-foreground" />
                        ຂໍ້ມູນບັນທຶກ
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <div>
                        <div className="text-muted-foreground text-xs uppercase tracking-wider">
                          ລະຫັດບັນທຶກ
                        </div>
                        <div className="font-mono text-xs">
                          {formatLogId(item.id)}-{item.id.slice(0, 8)}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs uppercase tracking-wider">
                          ປະເພດຕາຕະລາງ
                        </div>
                        <div>
                          {sessionTypeLabels[item.sessionType] ??
                            item.sessionType}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs uppercase tracking-wider">
                          ຜູ້ປ່ຽນແປງ
                        </div>
                        <div className="flex items-center gap-1.5">
                          <User className="size-4 shrink-0 text-muted-foreground" />
                          <span>
                            {item.changedByUserName ?? item.changedBy}{" "}
                            <span className="text-muted-foreground">
                              (ລະຫັດ: {item.changedBy})
                            </span>
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs uppercase tracking-wider">
                          ວັນທີ/ເວລາ
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Clock className="size-4 shrink-0 text-muted-foreground" />
                          <span>
                            {item.changedAt
                              ? format(
                                  new Date(item.changedAt),
                                  "MMM d, yyyy - HH:mm:ss",
                                )
                              : "-"}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Star className="size-4 text-muted-foreground" />
                        ບໍລິບົດຕາຕະລາງ
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <div>
                        <div className="text-muted-foreground text-xs uppercase tracking-wider">
                          ຫົວຂໍ້ / ຕາຕະລາງ
                        </div>
                        {item.entityLink ? (
                          <a
                            href={item.entityLink}
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            {item.sessionInfo}
                            <ExternalLink className="size-3.5 shrink-0" />
                          </a>
                        ) : (
                          <span>{item.sessionInfo || "-"}</span>
                        )}
                      </div>
                      <div className="rounded-md border border-primary/20 bg-primary/5 p-2 text-muted-foreground text-xs">
                        ບັນທຶກນີ້ບັນທຶກການປ່ຽນແປງຕາຕະລາງ.
                        ການປ່ຽນແປງໃໝ່ອາດສົ່ງແຈ້ງເຕືອນຕາມລະບົບ.
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right column */}
                <div className="space-y-6 md:col-span-2">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <FileText className="size-4 text-muted-foreground" />
                        ການປຽບທຽບການປ່ຽນແປງ
                      </CardTitle>
                      <Button variant="secondary" size="sm" className="text-xs">
                        ລາຍລະອຽດວິຊາກິດ
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto rounded border text-sm">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                                ຊື່ຟີວ
                              </th>
                              <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                                ຄ່າເກົ່າ
                              </th>
                              <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                                ຄ່າໃໝ່
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b last:border-0">
                              <td className="px-3 py-2 font-medium">
                                {changeTypeLabels[item.changeType] ??
                                  item.changeType}
                              </td>
                              <td className="px-3 py-2 font-mono text-xs">
                                {item.oldValue ?? "-"}
                              </td>
                              <td className="px-3 py-2 font-mono text-primary text-xs">
                                {item.newValue ?? "-"}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <MessageSquare className="size-4 text-muted-foreground" />
                        ເຫດຜົນການປ່ຽນແປງ
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <p className="wrap-break-word whitespace-pre-wrap text-muted-foreground">
                        {item.reason || "-"}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </>
          )}
        </QueryState>
      </Main>
    </>
  );
}
